import CloseIcon from '@mui/icons-material/Close';
import { memo, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  IconButton,
  TextField,
  Typography,
  Tooltip,
} from '@mui/material';
import { useBatchEdit } from './context';
import {
  dialogBaseTitleSx,
  dialogBasePaperSx,
  dialogBaseContentSx,
  dialogBaseActionsSx,
} from 'screens/styles/DialogStyles';
import { useSearchItemsSafe } from 'components/Search/components/SearchItemsProvider';
import { useCosmos } from 'providers/contexts/CosmosContext';
import {
  PermissionInfo,
  checkBatchEditSearchPermission,
  checkSiteAdminPermission,
} from 'utility/permissions';

export const BatchEditConfirm = memo(() => {
  const {
    confirmationOpen,
    setConfirmationOpen,
    setModifySearchResultsConfirm,
    handleSubmit,
    totalItemCount,
    effectiveItemIds,
  } = useBatchEdit();
  const { sessionConfig } = useCosmos();
  const { t } = useTranslation();
  const [confirmationInput, setConfirmationInput] = useState('');
  const [isConfirmEnabled, setIsConfirmEnabled] = useState(false);

  // Use safe hook that doesn't throw when outside search context
  const searchItemsContext = useSearchItemsSafe();

  // Prefer search context values when available, otherwise use batch edit context values
  const dataLength = searchItemsContext?.data?.length ?? effectiveItemIds.length ?? 0;
  const contextTotal = searchItemsContext?.total ?? totalItemCount ?? 0;
  const totalResults =
    (contextTotal || dataLength) && (contextTotal > 0 || dataLength)
      ? contextTotal < dataLength
        ? dataLength
        : contextTotal
      : 0;

  const { granted: userIsAdmin } = checkSiteAdminPermission(sessionConfig);
  const { granted: canBatchEditSearch, name: batchPermName } =
    checkBatchEditSearchPermission(sessionConfig);

  const permState: PermissionInfo = useMemo(() => {
    if (userIsAdmin || canBatchEditSearch) {
      return {
        info: undefined,
        disabled: false,
      };
    }
    return {
      info: t('blox.permissions.msg.required_short', {
        name: batchPermName,
      }),
      disabled: true,
    };
  }, [batchPermName, canBatchEditSearch, t, userIsAdmin]);

  const handleClose = () => {
    setConfirmationOpen(false);
    setConfirmationInput('');
  };

  const handleConfirm = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (confirmationInput === totalResults.toString()) {
      handleSubmit(event);
      handleClose();
    }
  };

  useEffect(() => {
    setIsConfirmEnabled(confirmationInput === totalResults.toString());

    if (isConfirmEnabled) {
      setModifySearchResultsConfirm(true);
    } else {
      setModifySearchResultsConfirm(false);
    }
  }, [confirmationInput, totalResults, isConfirmEnabled, setModifySearchResultsConfirm]);

  return (
    <Dialog
      open={confirmationOpen}
      onClose={() => handleClose()}
      aria-labelledby="batch-edit-dialog-title"
      aria-describedby="batch-edit-dialog-description"
      fullWidth
      slotProps={{
        paper: {
          component: 'form',
          onSubmit: handleConfirm,
          sx: dialogBasePaperSx,
        },
      }}
    >
      <DialogTitle
        id="batch-edit-dialog-title"
        data-testid="batch-edit-dialog-title"
        sx={dialogBaseTitleSx}
      >
        {t('blox.batch_edit.label')}
        <IconButton
          data-testid="batch-edit-dialog-close-button"
          onClick={() => handleClose()}
          sx={{ position: 'absolute', right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={dialogBaseContentSx}>
        <Typography sx={{ mb: 2 }}>
          {t('blox.batch_edit.confirm.change_count', { count: totalResults })}
        </Typography>
        <Typography sx={{ mb: 2 }}>
          {t('blox.batch_edit.confirm.type_count', { count: totalResults })}
        </Typography>
        <TextField
          id="confirmation-input"
          data-testid="confirmation-input"
          type="number"
          autoFocus
          fullWidth
          value={confirmationInput}
          onChange={(e) => {
            const value = e.target.value.replace(/^0+(?!$)/, ''); // removes leading 0s
            setConfirmationInput(value);
          }}
          placeholder={t('blox.batch_edit.confirm.input_placeholder')}
          variant="outlined"
          error={confirmationInput !== '' && !isConfirmEnabled}
          helperText={
            confirmationInput !== '' && !isConfirmEnabled
              ? t('blox.batch_edit.confirm.input_error')
              : ''
          }
          sx={{ mt: 1 }}
          slotProps={{
            htmlInput: {
              min: 1,
              inputMode: 'numeric',
              pattern: '[0-9]*',
            },
          }}
        />
      </DialogContent>
      <DialogActions sx={dialogBaseActionsSx}>
        <Button
          data-testid="batch-cancel-button"
          onClick={() => handleClose()}
        >
          {t('blox.actions.cancel')}
        </Button>
        <Tooltip title={permState.info}>
          <span>
            <Button
              data-testid="batch-confirm-button"
              type="submit"
              variant="contained"
              disabled={permState.disabled || !isConfirmEnabled}
            >
              {t('blox.actions.confirm')}
            </Button>
          </span>
        </Tooltip>
      </DialogActions>
    </Dialog>
  );
});

BatchEditConfirm.displayName = 'BatchEditConfirm';
